import { Component } from '@angular/core';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { ThemeService } from './services/theme.service';
import { NotificationsService } from './services/notifications/notifications.service';
import { AlertService } from './utils/swal-fire';
import { MobileInputDiagnosticsService } from './services/mobile-input-diagnostics.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  sidebarOculto = false;
  loading = false;
  private readonly SIDEBAR_OCULTO_KEY = 'zivo_sidebar_oculto';

  constructor(
    public router: Router,
    private themeService: ThemeService,
    private notificationsService: NotificationsService,
    private alertService: AlertService,
    private readonly mobileDiagnostics: MobileInputDiagnosticsService,
  ) {
    mobileDiagnostics.start(this.router);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.loading = true;
        return;
      }

      if (event instanceof NavigationEnd) {
        this.loading = false;

        if (this.isMobileViewport()) {
          this.sidebarOculto = true;
        }
        return;
      }

      if (event instanceof NavigationCancel) {
        this.loading = false;
        return;
      }

      if (event instanceof NavigationError) {
        this.loading = false;

        if (!navigator.onLine && this.isMobileViewport()) {
          this.alertService.toast(
            'Sin conexión · continúe con los módulos disponibles offline',
            'warning',
          );
          return;
        }

        if (!navigator.onLine) {
          this.alertService.loading(
            'Sin conexión',
            'ZIVO intentará reconectarse. En Control de Seguridad puede continuar con la información preparada offline.',
            'warning',
          );
          return;
        }

        this.alertService.error(
          'Error al cargar la página',
          'No fue posible completar la navegación.',
        );
      }
    });
  }

  title = 'construapp-frontend';

  private isMobileViewport(): boolean {
    return window.matchMedia('(max-width: 768px)').matches ||
      document.documentElement.classList.contains('zivo-pwa-phone');
  }

  mostrarTopBar(): boolean {
    const path = this.router.url.split('?')[0];

    const hiddenExact = ['/login', '/registro', '/recuperar', '/instalar', '/404'];
    if (hiddenExact.includes(path)) return false;

    if (
      path.startsWith('/prospect-external-forms/') ||
      path.startsWith('/public/update-contact-info') ||
      path.startsWith('/public/zkbio-face/') ||
      path.startsWith('/postulaciones/') ||
      path.startsWith('/postulacion/documentacion/') ||
      path.startsWith('/restaurante/alimentacion/')
    ) {
      return false;
    }

    return true;
  }

  ngOnInit(): void {
    const sidebarOcultoStorage = localStorage.getItem(this.SIDEBAR_OCULTO_KEY);

    if (this.isMobileViewport()) {
      this.sidebarOculto = true;
    } else if (sidebarOcultoStorage !== null) {
      this.sidebarOculto = sidebarOcultoStorage === 'true';
    }

    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    const token = params.get('token');

    if (token) {
      localStorage.setItem('token', token);
      this.router.navigate(['/home']);
    }

    if (redirect) {
      localStorage.setItem('redirect', redirect);
    }

    this.themeService.cargarTemaDesdeStorage();

    window.addEventListener('offline', () => {
      if (this.isMobileViewport()) {
        // En campo la falta de señal no debe bloquear la PWA.
        this.alertService.toast(
          'Sin conexión · trabajando en modo offline',
          'warning',
        );
        return;
      }

      // En escritorio sí mantenemos un aviso visible hasta recuperar red.
      this.alertService.loading(
        'Sin conexión',
        'Intentando conectarse nuevamente. Control de Seguridad puede continuar con la información preparada offline.',
        'warning',
      );
    });

    window.addEventListener('online', () => {
      // Cierra cualquier modal de desconexión de escritorio.
      this.alertService.close();
      this.alertService.toast('Conexión restaurada', 'success');
    });
  }

  onSidebarToggle(menuVisible: boolean): void {
    const before = !this.sidebarOculto;
    this.sidebarOculto = !menuVisible;
    this.mobileDiagnostics.menuHandled(before, menuVisible);

    if (!this.isMobileViewport()) {
      localStorage.setItem(
        this.SIDEBAR_OCULTO_KEY,
        String(this.sidebarOculto),
      );
    }
  }
}
